import {
  AutocompleteInput,
  Button,
  maxLength,
  minLength,
  ReferenceInput,
  required,
  SelectInput,
  TextInput,
  useNotify,
  useRefresh,
  FormDataConsumer,
} from "react-admin";
import FormModal from "server/components/Modals/FormModal";
import Edit from "@mui/icons-material/Edit";
import { Grid } from "@mui/material";
import { accountRequests } from "api/accountApi";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { GenderOptionsId, PersonIdTypes } from "utils/choices";
import { nameCyrillicRegexValidator, nameLatinRegexValidator, validateSecondName } from "utils/validations";
import { administrationRequests } from "api/administrationApi";
import { BULGARIA_COUNTRY_ID } from "utils/constants";
import { useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";
import dataProvider from "server/dataProvider";

const FormContent = ({ record, setIsConfirmEnabled }) => {
  const {
    setValue,
    formState: { isDirty: formDirty },
  } = useFormContext();

  useEffect(() => {
    setIsConfirmEnabled(formDirty);
  }, [formDirty, setIsConfirmEnabled]);

  return (
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            label="Име"
            source="userInfo.firstName"
            validate={[required(), minLength(2), maxLength(30), nameCyrillicRegexValidator]}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            label="Презиме"
            source="userInfo.secondName"
            // Keep "" (RA parses it to null by default) — the backend clears the field only on "", null is ignored.
            parse={v => v}
            validate={[validateSecondName, nameCyrillicRegexValidator]}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            label="Фамилия"
            source="userInfo.lastName"
            validate={[required(), minLength(2), maxLength(30), nameCyrillicRegexValidator]}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            label="Име на латиница"
            source="userInfo.firstNameEn"
            validate={[required("Задължително"), minLength(2), maxLength(30), nameLatinRegexValidator]}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            label="Презиме на латиница"
            source="userInfo.secondNameEn"
            // Keep "" (RA parses it to null by default) — the backend clears the field only on "", null is ignored.
            parse={v => v}
            validate={[validateSecondName, nameLatinRegexValidator]}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            label="Фамилия на латиница"
            source="userInfo.lastNameEn"
            validate={[required("Задължително"), minLength(2), maxLength(30), nameLatinRegexValidator]}
            fullWidth
          />
        </Grid>
        <FormDataConsumer>
          {({ formData }) => (
            <>
              <Grid
                item
                xs={12}
                md={4}
              >
                <SelectInput
                  source="userInfo.personIdType"
                  choices={PersonIdTypes}
                  label="Тип идентификатор"
                  onChange={e => {
                    if (!e?.target?.value) setValue("userInfo.personId", null);
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <TextInput
                  label={PersonIdTypes.find(x => x.id === formData?.userInfo?.personIdType)?.name || "Идентификатор"}
                  source="userInfo.personId"
                  validate={formData.userInfo.personIdType ? required("Задължително") : undefined}
                  fullWidth
                />
              </Grid>
            </>
          )}
        </FormDataConsumer>
        <Grid
          item
          xs={12}
          md={4}
        >
          <SelectInput
            label="Пол"
            source="userInfo.gender"
            choices={GenderOptionsId}
            validate={[required()]}
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            label="Телефон"
            source="userInfo.contactPhone"
            fullWidth
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <CustomDateInput
            label="Дата на раждане"
            source="userInfo.birthDate"
            fullWidth
          />
        </Grid>
        <FormDataConsumer>
          {({ formData }) => (
            <>
              {formData?.userInfo?.birthCountryId === BULGARIA_COUNTRY_ID ? (
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <ReferenceInput
                    source="userInfo.birthSettlementId"
                    reference={administrationRequests.getSettlementsSelect()}
                  >
                    <AutocompleteInput
                      label="Град на раждане"
                      optionText="nameBg"
                      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                      fullWidth
                    />
                  </ReferenceInput>
                </Grid>
              ) : (
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <TextInput
                    label="Населено място"
                    source="userInfo.birthSettlement"
                    validate={required("Задължително")}
                  />
                </Grid>
              )}
              <Grid
                item
                xs={12}
                md={4}
              >
                <ReferenceInput
                  source="userInfo.birthCountryId"
                  reference={administrationRequests.getCountriesSelect()}
                >
                  <AutocompleteInput
                    label="Държава на раждане"
                    optionText="nameBg"
                    filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                    fullWidth
                  />
                </ReferenceInput>
              </Grid>
            </>
          )}
        </FormDataConsumer>
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="userInfo.citizenshipId"
            reference={administrationRequests.getCitizenshipSelect()}
          >
            <SelectInput
              defaultValue={record.userInfo.citizenship}
              label="Тип гражданство"
              optionText="name"
              fullWidth
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            source="userInfo.primaryCitizenshipCountryId"
            reference={administrationRequests.getCountriesSelect()}
          >
            <AutocompleteInput
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
              optionText="nameBg"
              label="Основно гражданство"
              fullWidth
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            reference={administrationRequests.getCountriesSelect()}
            source="userInfo.secondaryCitizenshipCountryId"
          >
            <AutocompleteInput
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
              optionText="nameBg"
              label="Второ гражданство"
              fullWidth
            />
          </ReferenceInput>
        </Grid>
      </Grid>
  );
};

const EditPersonalInfoModal = ({ record }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(false);
  const toDateOnly = value => (value ? value.split("T")[0] : null);

  const handleSubmit = async data => {
    const { userInfo } = data;
    const payload = {
      firstName: userInfo.firstName,
      secondName: userInfo.secondName,
      lastName: userInfo.lastName,
      firstNameEn: userInfo.firstNameEn,
      secondNameEn: userInfo.secondNameEn,
      lastNameEn: userInfo.lastNameEn,
      personIdType: userInfo.personIdType,
      personId: userInfo.personId,
      gender: userInfo.gender,
      contactPhone: userInfo.contactPhone,
      citizenshipId: userInfo.citizenshipId,
      birthDate: toDateOnly(userInfo.birthDate),
      birthCountryId: userInfo.birthCountryId,
      birthSettlementId: userInfo.birthSettlementId,
      birthSettlement: userInfo.birthSettlement,
      primaryCitizenshipCountryId: userInfo.primaryCitizenshipCountryId,
      secondaryCitizenshipCountryId: userInfo.secondaryCitizenshipCountryId,
    };
    await dataProvider.customRequest(accountRequests.updateUserPersonalData(record?.userId), payload, "PATCH");
    refresh();
    notify("Успешна редакция на лични данни", { type: "success" });
  };

  return (
    <FormModal
      titleContent="Редакция на лични данни"
      onConfirm={handleSubmit}
      confirmEnabled={isConfirmEnabled}
      dialogSize="md"
      triggerButton={
        <Button
          label="Редакция на лични данни"
          variant="contained"
          size="small"
          startIcon={<Edit />}
        />
      }
    >
      <FormContent
        record={record}
        setIsConfirmEnabled={setIsConfirmEnabled}
      />
    </FormModal>
  );
};

export default EditPersonalInfoModal;
